import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LayoutDashboard, Calendar, Megaphone, BarChart3, Users, UserPlus, User, Shield, Sparkles, Kanban, GraduationCap, Mail, Video, CreditCard, Settings, Receipt } from "lucide-react";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import { useAuth } from "@/AuthContext";
import AIAssistantDrawer from "../AIAssistantDrawer";

const ROUTE_META = {
  "/mission-control": { title: "Mission Control", icon: LayoutDashboard },
  "/events": { title: "Events", icon: Calendar },
  "/advocacy": { title: "Advocacy", icon: Megaphone },
  "/program": { title: "Program Insights", icon: BarChart3 },
  "/roster": { title: "Roster", icon: Users },
  "/profile": { title: "Profile", icon: User },
  "/invite": { title: "Invite", icon: UserPlus },
  "/admin": { title: "Admin", icon: Shield },
  "/admin/dashboard": { title: "Admin Dashboard", icon: Shield },
  "/admin/integrations": { title: "Integrations", icon: Shield },
  "/admin/universities": { title: "Universities", icon: GraduationCap },
  "/board": { title: "Dashboard", icon: LayoutDashboard },
  "/pipeline": { title: "My Schools", icon: Kanban },
  "/schools": { title: "Find Schools", icon: GraduationCap },
  "/calendar": { title: "Calendar", icon: Calendar },
  "/inbox": { title: "Inbox", icon: Mail },
  "/highlights": { title: "Highlights", icon: Video },
  "/analytics": { title: "Analytics", icon: BarChart3 },
  "/athlete-profile": { title: "Profile", icon: User },
  "/account": { title: "Account", icon: CreditCard },
  "/billing": { title: "Billing", icon: Receipt },
  "/athlete-settings": { title: "Settings", icon: Settings },
};

function getRouteMeta(pathname) {
  const exact = ROUTE_META[pathname];
  if (exact) return exact;
  for (const [path, meta] of Object.entries(ROUTE_META)) {
    if (pathname.startsWith(path)) return meta;
  }
  return { title: "CapyMatch", icon: LayoutDashboard };
}

export default function AppLayout({ children, title, icon }) {
  const location = useLocation();
  const routeMeta = getRouteMeta(location.pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cm-sidebar-collapsed") === "true");
  const [aiOpen, setAiOpen] = useState(false);
  const { user, effectiveRole } = useAuth();
  const role = effectiveRole || user?.role;
  const isAthlete = role === "athlete" || role === "parent";

  // Allow embedded components (e.g. JourneyPage's MobileCommandDock) to open
  // the AI assistant without prop-drilling. Mirrors the cm:open-notes pattern.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOpen = () => setAiOpen(true);
    window.addEventListener("cm:open-ai-assistant", onOpen);
    return () => window.removeEventListener("cm:open-ai-assistant", onOpen);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("cm-sidebar-collapsed", String(next));
      return next;
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--cm-bg)" }}>
      {sidebarOpen && (
        <div className="fixed inset-0 z-[55] lg:hidden" style={{ backgroundColor: "var(--cm-overlay)" }} onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={collapsed} onToggleCollapse={toggleCollapsed} />

      <div className="flex flex-col min-h-screen transition-[margin] duration-300" style={{ marginLeft: `var(--sidebar-offset, 0px)` }}>
        <style>{`@media (min-width: 1024px) { :root { --sidebar-offset: ${collapsed ? "64px" : "220px"}; } }`}</style>
        <TopBar
          title={title || routeMeta.title}
          icon={icon || routeMeta.icon}
          onMenuToggle={() => setSidebarOpen((p) => !p)}
        />
        <main className="flex-1 px-2 py-4 sm:px-6 sm:py-6 max-w-[1200px]" style={{ overflowX: "clip" }}>
          {children}
        </main>
      </div>

      {/* AI Assistant FAB for athletes — restrained, supporting utility.
          Hidden on mobile when JourneyPage's MobileCommandDock is mounted
          (body[data-journey-mobile-dock="1"]) so the two never compete. */}
      {isAthlete && (
        <button onClick={() => setAiOpen(true)}
          className="ai-fab-global fixed w-11 h-11 sm:w-12 sm:h-12 rounded-full z-[80] flex items-center justify-center transition-all hover:scale-105 active:scale-95"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)",
            right: "18px",
            background: "#1e293b",
            boxShadow: "0 4px 14px rgba(15,23,42,0.18), 0 1px 2px rgba(15,23,42,0.10)",
          }}
          data-testid="ai-fab">
          <Sparkles className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-white" style={{ opacity: 0.92 }} />
        </button>
      )}
      {isAthlete && <AIAssistantDrawer isOpen={aiOpen} onClose={() => setAiOpen(false)} />}
    </div>
  );
}
